import { useDeleteIntraofficeNote } from '@/apis/agency/intraofficeNotes/hook';
import { IntraofficeNoteResponse } from '@/apis/agency/intraofficeNotes/types';
import { timeAgo } from '@/utils/helperFunctions';
import { Avatar, Box, Chip, IconButton, lighten, Paper, Stack, Typography, useTheme } from '@mui/material';
import { GoClock } from 'react-icons/go';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';

import { useNavigate } from 'react-router-dom';

interface Props {
  notes: IntraofficeNoteResponse[];
}

export default function NotesList({ notes }: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const deleteMutation = useDeleteIntraofficeNote();

  return (
    <Stack spacing={2}>
      {notes.map((note) => (
        <Paper
          key={note.id}
          elevation={0}
          sx={{
            p: '16px',
            borderRadius: '8px',
            border: '1px solid #E4E7EC',
            transition: '0.2s',
            '&:hover': {
              boxShadow: '0 4px 14px rgba(0,0,0,0.06)'
            }
          }}
        >
          <Stack direction='row' spacing={1}>
            {/* Avatar */}
            {note?.user_avatar ? (
              <Avatar
                src={note?.user_avatar}
                alt={note?.user_name || 'User'}
                sx={{
                  width: '48px',
                  height: '48px'
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: '48px',
                  height: '48px',
                  bgcolor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  fontWeight: 600
                }}
              >
                {getInitials(note.user_name)}
              </Avatar>
            )}

            {/* Content */}
            <Box flex={1}>
              {/* Top Row */}
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' spacing={1}>
                  <Typography fontWeight={400} fontSize={14}>
                    {note.user_name}
                  </Typography>
                  <Stack direction='row' alignItems='center' spacing={0.5} fontSize={12}>
                    <GoClock />
                    <Typography color='text.secondary' fontSize={12}>
                      {timeAgo(note.updated_at)}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack direction='row'>
                  <IconButton size='small' onClick={() => navigate(`${note.id}`)}>
                    <MdOutlineEdit />
                  </IconButton>

                  <IconButton
                    size='small'
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(note.id.toString())}
                  >
                    <RiDeleteBin6Line style={{ color: 'red' }} />
                  </IconButton>
                </Stack>
              </Stack>

              <Stack>
                {/* Title */}
                <Typography fontWeight={500} fontSize={14}>
                  {note.title}
                </Typography>

                {/* Description */}
                <Typography color='text.secondary' fontWeight={400} fontSize={12} lineHeight={1.3} marginTop={0.2}>
                  {note.note}
                </Typography>
              </Stack>

              {/* Chips */}
              {(note.business_name || note.agent_name) && (
                <Stack direction='row' spacing={1} mt={1}>
                  {note.business_name && (
                    <Chip
                      label={note.business_name}
                      size='small'
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: theme.palette.primary.main,
                        border: `1px solid ${lighten(theme.palette.primary.main, 0.7)}`,
                        borderRadius: '4px',
                        fontWeight: 500
                      }}
                    />
                  )}

                  {note.agent_name && (
                    <Chip
                      label={note.agent_name}
                      size='small'
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: theme.palette.primary.main,
                        border: `1px solid ${lighten(theme.palette.primary.main, 0.7)}`,
                        borderRadius: '4px',
                        fontWeight: 500
                      }}
                    />
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

export const getInitials = (name: string) =>
  name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
